// Device timezone (common/22) — the curated "zone → POSIX TZ string" table the
// tz picker offers. POSIX-only (e.g. "CST-8", "EST5EDT,M3.2.0,M11.1.0"); this is
// NOT the IANA WeeklySchedule timezone (ADR-054) — different concept, do not mix.
//
// Per common/22 O6 the picker should cover ALL timezones: this table is intended
// to be GENERATED FROM tzdata (each IANA canonical zone → its POSIX TZ string,
// incl. DST rules) and checked in; regenerate on each tzdata release. The set
// below is a representative starter covering the major offsets/DST variants —
// expand to the full tzdata set via the generator (scripts/gen-timezones.ts,
// `zdump -v` / zoneinfo POSIX trailer). Kept sorted by UTC offset for the picker.
//
// `iana` is used ONLY to pre-select from the browser/OS-detected zone name; only
// `posix` is ever sent to the device.

export interface TimezoneOption {
  /** IANA canonical zone name — for browser pre-selection only, never sent. */
  iana: string;
  /** Human label shown in the picker. */
  label: string;
  /** POSIX TZ string — the ONLY value sent to the device (op:set strVal). */
  posix: string;
}

// Representative starter set (generated shape). Expand to full tzdata coverage.
export const TIMEZONES: readonly TimezoneOption[] = [
  { iana: 'Pacific/Midway', label: '(GMT-11) Midway', posix: 'SST11' },
  { iana: 'Pacific/Honolulu', label: '(GMT-10) Honolulu', posix: 'HST10' },
  { iana: 'America/Anchorage', label: '(GMT-9) Anchorage', posix: 'AKST9AKDT,M3.2.0,M11.1.0' },
  { iana: 'America/Los_Angeles', label: '(GMT-8) Los Angeles', posix: 'PST8PDT,M3.2.0,M11.1.0' },
  { iana: 'America/Denver', label: '(GMT-7) Denver', posix: 'MST7MDT,M3.2.0,M11.1.0' },
  { iana: 'America/Phoenix', label: '(GMT-7) Phoenix (no DST)', posix: 'MST7' },
  { iana: 'America/Chicago', label: '(GMT-6) Chicago', posix: 'CST6CDT,M3.2.0,M11.1.0' },
  { iana: 'America/New_York', label: '(GMT-5) New York', posix: 'EST5EDT,M3.2.0,M11.1.0' },
  { iana: 'America/Sao_Paulo', label: '(GMT-3) São Paulo', posix: '<-03>3' },
  { iana: 'Atlantic/Azores', label: '(GMT-1) Azores', posix: 'AZOT1AZOST,M3.5.0/0,M10.5.0/1' },
  { iana: 'Etc/UTC', label: '(GMT+0) UTC', posix: 'UTC0' },
  { iana: 'Europe/London', label: '(GMT+0) London', posix: 'GMT0BST,M3.5.0/1,M10.5.0' },
  { iana: 'Europe/Paris', label: '(GMT+1) Paris', posix: 'CET-1CEST,M3.5.0,M10.5.0/3' },
  { iana: 'Europe/Athens', label: '(GMT+2) Athens', posix: 'EET-2EEST,M3.5.0/3,M10.5.0/4' },
  { iana: 'Europe/Moscow', label: '(GMT+3) Moscow', posix: 'MSK-3' },
  { iana: 'Asia/Dubai', label: '(GMT+4) Dubai', posix: '<+04>-4' },
  { iana: 'Asia/Karachi', label: '(GMT+5) Karachi', posix: 'PKT-5' },
  { iana: 'Asia/Kolkata', label: '(GMT+5:30) Kolkata', posix: 'IST-5:30' },
  { iana: 'Asia/Dhaka', label: '(GMT+6) Dhaka', posix: '<+06>-6' },
  { iana: 'Asia/Bangkok', label: '(GMT+7) Bangkok', posix: '<+07>-7' },
  { iana: 'Asia/Shanghai', label: '(GMT+8) Shanghai', posix: 'CST-8' },
  { iana: 'Asia/Taipei', label: '(GMT+8) Taipei', posix: 'CST-8' },
  { iana: 'Asia/Hong_Kong', label: '(GMT+8) Hong Kong', posix: 'HKT-8' },
  { iana: 'Asia/Singapore', label: '(GMT+8) Singapore', posix: '<+08>-8' },
  { iana: 'Asia/Tokyo', label: '(GMT+9) Tokyo', posix: 'JST-9' },
  { iana: 'Asia/Seoul', label: '(GMT+9) Seoul', posix: 'KST-9' },
  { iana: 'Australia/Sydney', label: '(GMT+10) Sydney', posix: 'AEST-10AEDT,M10.1.0,M4.1.0/3' },
  { iana: 'Pacific/Auckland', label: '(GMT+12) Auckland', posix: 'NZST-12NZDT,M9.5.0,M4.1.0/3' },
];

const POSIX_BY_IANA: ReadonlyMap<string, string> = new Map(TIMEZONES.map((t) => [t.iana, t.posix]));

/** Detect the browser/OS timezone name (IANA); "" if unavailable. */
export function browserTimeZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * Best-guess POSIX for the browser's detected zone (for picker pre-selection).
 * Returns the exact-match POSIX when the detected IANA zone is in the table,
 * else "" (caller leaves the picker unset). Never used as the sent value without
 * the user confirming a picker option.
 */
export function browserPosixDefault(): string {
  return POSIX_BY_IANA.get(browserTimeZoneName()) ?? '';
}

/** Human label for a stored POSIX value (for read-only display); falls back to the raw POSIX. */
export function labelForPosix(posix: string): string {
  return TIMEZONES.find((t) => t.posix === posix)?.label ?? posix;
}
